<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal size="lg" :visible="modal.show" :data="model"
                   :title="model.uuid ? $t(`Update Customer`) : $t(`Create New Customer`)"
                   @show="onShow" @hidden="onHidden" @ok.prevent="handleSubmit(onSubmit)"
                   :show-confirmation="showConfirmation" :ok-title="$t('Submit')"
                   :is-loading="loading" scrollable>
      <div class="row">
        <div class="col-md-6">
          <input-widget :autofocus="true" :model="model" attribute="first_name"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="last_name"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="middle_name"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="company_name"/>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-6 col-md-4 col-lg-2 align-items-center">
          <input-widget is-switch :model="model" attribute="is_company" type="checkbox"/>
        </div>
        <div class="col-sm-6 col-md-4 col-lg-3  align-items-center">
          <input-widget is-switch :model="model" attribute="enable_estimate_request" type="checkbox"/>
        </div>
        <div class="col-sm-6 col-md-8 col-lg-5 align-items-center">
          <input-widget is-switch :model="model" attribute="sms_optin" type="checkbox"/>
        </div>
        <div class="col-sm-6 col-md-4 col-lg-2 align-items-center" v-if="customerDefaultEmailDomain">
          <input-widget is-switch :model="model" attribute="no_email" type="checkbox" @change="onNoEmailCheck"/>
        </div>
        <div class="col-sm-6 col-md-4 col-lg-2 align-items-center">
          <input-widget is-switch :model="model" attribute="deceased" type="checkbox"/>
        </div>
        <div class="col-6 align-items-center">
          <input-widget is-switch :model="model" attribute="block_requesting_condition_reports" type="checkbox"/>
        </div>
      </div>
      <div class="row">
        <div class="col-md-4" v-if="hasMoreThanOneLanguage">
          <language-code-select-input-widget :model="model" attribute="preferred_language_code"/>
        </div>
        <div class="col-md-4">
          <customer-title-select-input-widget :model="model" :preferred-language-code="model.preferred_language_code"
                                              attribute="title"/>
        </div>
        <div class="col-md-4">
          <input-widget :model="model"
                        attribute="status"
                        value-field="value"
                        display-field="${item.key}"
                        type="select2"
                        :items="model.uuid ? customerStatuses : creationCustomerStatuses"/>
        </div>
        <div class="col-md-4">
          <input-widget :model="model" attribute="personal_id"/>
        </div>
        <div class="col-md-4">
          <input-widget :model="model" attribute="initials"/>
        </div>
        <div class="col-md-4">
          <input-widget :model="model" attribute="gender" value-field="value"
                        display-field="${item.key}" type="select2" :items="customerGenders"/>
        </div>
        <div class="col-md-4">
          <client-tier-select-input-widget :model="model" attribute="client_tier" />
        </div>
      </div>
      <div class="row">
        <div class="col-md-6">
          <input-widget :model="model" attribute="vendor_lead_status" value-field="value"
                        display-field="${item.key}" type="select2" :items="customerLeadStatuses"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="buyer_lead_status" value-field="value"
                        display-field="${item.key}" type="select2" :items="customerLeadStatuses"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="phone1" @change="onPhoneChange"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="phone2"/>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6">
          <input-widget :model="model" @input="onEmailInput('email')" attribute="email" @change="onEmailChange"
                        :rules="emailRules" :disabled="model.no_email"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" @input="onEmailInput('email2')" attribute="email2"/>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6">
          <input-widget :hint="true" :model="model" attribute="tags" type="tags" v-if="!tagsInputFormat"/>
          <input-widget :items="tagsInputDropdownValues"
                        value-field="value"
                        display-field="${item.label}"
                        type="select2"
                        :model="model"
                        attribute="tags"
                        :multiple="true"
                        v-if="tagsInputFormat === 'dropdown'"/>
          <input-widget :model="model" attribute="tags" v-if="tagsInputFormat === 'plain'"/>
        </div>
        <div class="col-md-6">
          <user-select-input-widget :model="model" attribute="account_managers_uuid"/>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6" v-if="customerTypes && customerTypes.length > 0">
          <input-widget :model="model"
                        attribute="type"
                        value-field="value"
                        display-field="${item.label}"
                        type="select2"
                        :items="customerTypes"/>
        </div>
        <div class="col-md-6" v-if="isIntermediaryCustomerTypesEnabled">
          <input-widget :model="model"
                        attribute="intermediary_type"
                        value-field="value"
                        display-field="${item.label}"
                        type="select2"
                        :items="intermediaryCustomerTypes"/>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6">
          <input-widget :items="customerAcquisitionSources"
                        value-field="value"
                        display-field="${item.key}"
                        type="select2"
                        :model="model"
                        attribute="acquisition_source"/>
        </div>
        <div class="col-md-6">
          <input-widget type="datepicker" :model="model" attribute="birth_date"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model"
                        attribute="correspondence"
                        value-field="value"
                        display-field="${item.key}"
                        type="select2"
                        :items="correspondenceValues"/>
        </div>
        <div class="col-md-6">
          <input-widget v-if="model.acquisition_source === 'Other (free text)'" :model="model"
                        attribute="acquisition_source_text"/>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <hr class="mt-0">
        </div>
      </div>

      <div class="row mb-3" v-if="$hasPermission('change_customeraddress')">
        <div class="col-md-12">
          <div class="card">
            <div class="card-header d-flex justify-content-between pb-1">
              <h5 @click="toggleShippingAddress" style="cursor:pointer; user-select: none">
                {{ $t(`Shipping/Collection Address`) }} </h5>
              <button class="btn btn-link pt-0"
                      @click.prevent="copyAddress(model.defaultBillingAddress, model.defaultShippingAddress, true)">
                {{ $t('Same as Billing') }}
              </button>
            </div>
            <spinner :show="loadShippingState"/>
            <div class="card-body pb-0" v-if="model.show_shipping_address && !loadShippingState">
              <div class="row">
                <div class="col-md-12">
                  <country-select-input-widget attribute="country_code" :model="model.defaultShippingAddress"
                                               :rules="computedRules['country_code']"/>
                </div>
                <div class="col-md-6">
                  <input-widget :model="model.defaultShippingAddress" attribute="address_1"
                                :rules="computedRules['address_1']"/>
                </div>
                <div class="col-md-6">
                  <input-widget :model="model.defaultShippingAddress" attribute="address_2"/>
                </div>
                <div class="col-md-6">
                  <state-input-widget v-if="isAvalaraEnabled && model.defaultShippingAddress.country_code === 'US'"
                                      :model="model.defaultShippingAddress" attribute="state"/>
                  <input-widget :model="model.defaultShippingAddress" attribute="state" v-else/>
                </div>
                <div class="col-md-6">
                  <county-input-widget
                      :model="model.defaultShippingAddress" attribute="county" object-uuid-name="county_obj_uuid"
                      :state-uuid="model.defaultShippingAddress.state_obj_uuid">
                  </county-input-widget>
                </div>
                <div class="col-md-6">
                  <city-input-widget
                      :model="model.defaultShippingAddress" attribute="city" object-uuid-name="city_obj_uuid"
                      :county-uuid="model.defaultShippingAddress.county_obj_uuid" :rules="computedRules['city']">
                  </city-input-widget>
                </div>
                <div class="col-md-6">
                  <post-code-input-widget
                      :model="model.defaultShippingAddress" attribute="zip_code" :src="CUSTOMER_FORM"
                      :rules="computedRules['zip_code']">
                  </post-code-input-widget>
                </div>
                <div class="col-md-6">
                  <input-widget :model="model.defaultShippingAddress" attribute="email"/>
                </div>
                <div class="col-md-6">
                  <input-widget :model="model.defaultShippingAddress" attribute="phone"/>
                </div>
                <div class="col-md-6">
                  <input-widget :model="model.defaultShippingAddress" attribute="first_name"/>
                </div>
                <div class="col-md-6">
                  <input-widget :model="model.defaultShippingAddress" attribute="last_name"/>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row mb-3"  v-if="$hasPermission('change_customeraddress')">
        <div class="col-md-12">
          <div class="card">
            <div class="card-header d-flex justify-content-between pb-1">
              <h5 @click="toggleBillingAddress" style="cursor:pointer; user-select: none">
                {{ $t(`Billing Address`) }} </h5>
              <button class="btn btn-link pt-0"
                      @click.prevent="copyAddress(model.defaultShippingAddress, model.defaultBillingAddress, false, true)">
                {{ $t('Same as Shipping') }}
              </button>
            </div>
            <spinner :show="loadBillingState"/>
            <div class="card-body pb-0" v-if="model.show_billing_address && !loadBillingState">
              <div class="row">
                <div class="col-md-12">
                  <country-select-input-widget attribute="country_code" :model="model.defaultBillingAddress"
                                               :rules="computedRulesForBillingAddress['country_code']"/>
                </div>
                <div class="col-md-6">
                  <input-widget :model="model.defaultBillingAddress" attribute="address_1"
                                :rules="computedRulesForBillingAddress['address_1']"/>
                </div>
                <div class="col-md-6">
                  <input-widget :model="model.defaultBillingAddress" attribute="address_2"/>
                </div>
                <div class="col-md-6">
                  <state-input-widget v-if="isAvalaraEnabled && model.defaultBillingAddress.country_code === 'US'"
                                      :model="model.defaultBillingAddress" attribute="state"/>
                  <input-widget :model="model.defaultBillingAddress" attribute="state" v-else/>
                </div>
                <div class="col-md-6">
                  <county-input-widget
                      :model="model.defaultBillingAddress" attribute="county" object-uuid-name="county_obj_uuid"
                      :state-uuid="model.defaultBillingAddress.state_obj_uuid">
                  </county-input-widget>
                </div>
                <div class="col-md-6">
                  <city-input-widget
                      :model="model.defaultBillingAddress" attribute="city" object-uuid-name="city_obj_uuid"
                      :county-uuid="model.defaultBillingAddress.county_obj_uuid"
                      :rules="computedRulesForBillingAddress['city']">
                  </city-input-widget>
                </div>
                <div class="col-md-6">
                  <post-code-input-widget
                      :model="model.defaultBillingAddress" attribute="zip_code" :src="CUSTOMER_FORM"
                      :rules="computedRulesForBillingAddress['zip_code']">
                  </post-code-input-widget>
                </div>
                <div class="col-md-6">
                  <input-widget :model="model.defaultBillingAddress" attribute="email"/>
                </div>
                <div class="col-md-6">
                  <input-widget :model="model.defaultBillingAddress" attribute="phone"/>
                </div>
                <div class="col-md-6">
                  <input-widget :model="model.defaultBillingAddress" attribute="first_name"/>
                </div>
                <div class="col-md-6">
                  <input-widget :model="model.defaultBillingAddress" attribute="last_name"/>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import InputWidget from "../../core/components/input-widget/InputWidget";
  import {createNamespacedHelpers} from "vuex";
  import ArtisioModal from "../../core/components/artisio-modal/ArtisioModal";
  import CustomerForm from "./models/CustomerForm.model";
  import CountrySelectInputWidget from "../../core/widgets/CountrySelectInputWidget";
  import PercentageInputWidget from "../../core/widgets/PercentageInputWidget";
  import CustomerTitleSelectInputWidget from "../../core/widgets/CustomerTitleSelectInputWidget"
  import {uuid} from "vue-uuid";

  import {
    CUSTOMER_FORM,
    SOURCE_BIDDERS_MODAL,
    SOURCE_CONDITION_REPORT_REQUEST_MODAL,
    SOURCE_CUSTOMER_DASHBOARD,
    SOURCE_CUSTOMER_VIEW,
    SOURCE_INVENTORY_DASHBOARD,
    SOURCE_RECEIPT_DASHBOARD,
    SOURCE_RECEIPT_MODAL,
    SOURCE_RESERVATION_DASHBOARD
  } from "../constants";
  import LanguageCodeSelectInputWidget from "@/core/widgets/LanguageCodeSelectInputWidget";
  import UserSelectInputWidget from "@/core/widgets/UserSelectInputWidget";
  import PostCodeInputWidget from "../../core/widgets/PostCodeInputWidget";
  import _ from "lodash";
  import {EMAIL_REGEX} from "@/plugins/vee-validate";
  import StateInputWidget from "../../core/widgets/StateInputWidget";
  import {GeographicStructureHelper} from "@/core/services/geographicStructureHelper";
  import CountyInputWidget from "../../core/widgets/CountyInputWidget";
  import CityInputWidget from "../../core/widgets/CityInputWidget";
  import Spinner from "../../core/components/common/Spinner";
  import ClientTierSelectInputWidget from "@/core/widgets/ClientTierSelectInputWidget.vue";

  const {mapState, mapActions} = createNamespacedHelpers('customer');
  const {mapActions: customerViewMapActions} = createNamespacedHelpers('customer/customerView');
  const {mapState: globalMapState, mapGetters: globalMapGetters} = createNamespacedHelpers('global');
  const {mapActions: inventoryMapActions} = createNamespacedHelpers('inventory');
  const {mapActions: receiptsMapActions} = createNamespacedHelpers('receipts');
  const {mapActions: reservationsMapActions} = createNamespacedHelpers('reservations');


  export default {
    name: "CustomerFormModal",
    components: {
      ClientTierSelectInputWidget,
      Spinner,
      CityInputWidget,
      CountyInputWidget,
      StateInputWidget,
      PostCodeInputWidget,
      UserSelectInputWidget,
      LanguageCodeSelectInputWidget,
      InputWidget, ArtisioModal, CountrySelectInputWidget, PercentageInputWidget, CustomerTitleSelectInputWidget
    },
    data() {
      return {
        loading: false,
        showConfirmation: true,
        model: new CustomerForm(),
        showBillingAddress: false,
        showShippingAddress: false,
        CUSTOMER_FORM: CUSTOMER_FORM,
        loadShippingState: false,
        loadBillingState: false,
        oldStatus: 'active'
      }
    },
    computed: {
      ...globalMapGetters(['isIntermediaryCustomerTypesEnabled', 'isAvalaraEnabled']),
      ...mapState({
        modal: state => state.modal,
        editObject: state => state.modal.object,
      }),
      ...globalMapState({
        enableNlRelations: state => state.enableNlRelations,
        customerAcquisitionSources: state => state.customerAcquisitionSources,
        customerStatuses: state => state.customerStatuses,
        customerLeadStatuses: state => state.customerLeadStatuses,
        customerGenders: state => state.customerGenders,
        customerTitles: state => state.customerTitles,
        hasMoreThanOneLanguage: state => state.hasMoreThanOneLanguage,
        creationCustomerStatuses: state => {
          return state.customerStatuses.filter(status => status.value !== 'blocked')
        },
        addressType: state => state.addressTypeToShowInCustomerEditForm,
        customerDefaultEmailDomain: state => state.customerDefaultEmailDomain,
        customerTypes: state => state.customerTypes,
        intermediaryCustomerTypes: state => state.intermediaryCustomerTypes,
        tagsInputFormat: state => state.tagsInputFormat,
        tagsInputDropdownValues: state => state.tagsInputDropdownValues,
        correspondenceValues: state => state.correspondenceValues,
      }),
      computedRules() {
        return this.computedRulesForAddress(this.model.defaultShippingAddress, this)
      },
      computedRulesForBillingAddress() {
        return this.computedRulesForAddress(this.model.defaultBillingAddress, this, false)
      },
      computedRulesForAddress: () => (address, vm, isShipping = true) => {
        let address2OrState = address.address_2 || address.state || address.state_obj_uuid;
        let address_1 = address.address_1;
        return {
          address_1: address2OrState || address.city || (address.country_code && address_1) || address.zip_code ? 'required' : '',
          city: address2OrState || address.address_1 || (address.country_code && address_1) || address.zip_code ? 'required' : '',
          country_code: address2OrState || address.city || address.address_1 || address.zip_code ? 'required' : '',
          zip_code: address2OrState || address.city || (address.country_code && address_1) || address.address_1 ? 'required' : '',
          state: isShipping && address.country_code === 'US' && (address.address_2 || address.city || (address.country_code && address_1) || address.zip_code) ? 'required' : '',
          state_obj_uuid: isShipping && address.country_code === 'US' && (address.address_2 || address.city || (address.country_code && address_1) || address.zip_code) ? 'required' : '',
        }
      },
      emailRules() {
        return this.model.no_email ? {regex: EMAIL_REGEX, max: 73} : {regex: EMAIL_REGEX, max: 73, required: true};
      }
    },
    watch: {
      editObject() {
        if (this.editObject && this.editObject.uuid) {

          let tmpCustomer = this.editObject;

          if (tmpCustomer.phone) {
            let phones = tmpCustomer.phone.split(',');
            tmpCustomer.phone1 = phones[0];
            tmpCustomer.phone2 = phones[1];
          }

          if (tmpCustomer.acquisition_source &&
              tmpCustomer.acquisition_source !== '' &&
              !this.customerAcquisitionSources.find(a => a.value === tmpCustomer.acquisition_source)) {

            tmpCustomer.acquisition_source_text = tmpCustomer.acquisition_source;
            tmpCustomer.acquisition_source = 'Other (free text)';
          }

          if (!this.tagsInputFormat || this.tagsInputFormat === 'dropdown') {
            tmpCustomer.tags = tmpCustomer.tags && tmpCustomer.tags.length > 0 ? tmpCustomer.tags.split(',') : [];
          }

          this.model = new CustomerForm(tmpCustomer);
          this.oldStatus = this.model.status
          this.showHideShippingBillingAddresses()
        }
      },
    },
    methods: {
      ...mapActions(['hideModal', 'createCustomer', 'updateCustomer', 'customerView/updateViewCustomer']),
      ...inventoryMapActions(['updateInventoryDashboardCustomer']),
      ...receiptsMapActions(['updateViewReceiptCustomer']),
      ...customerViewMapActions(['getAddressListAddresses']),
      ...reservationsMapActions({reservationsUpdateVendor: 'updateVendor'}),
      onEmailInput: _.debounce(function (key) {
        this.model[key] = this.model[key].replace(/ /g, '')
      }, 500),
      showHideShippingBillingAddresses() {
        this.model.show_shipping_address = this.addressType === "shipping"
        this.model.show_billing_address = this.addressType === "billing"
      },
      toggleShippingAddress() {
        this.model.show_shipping_address = !this.model.show_shipping_address
      },
      toggleBillingAddress() {
        this.model.show_billing_address = !this.model.show_billing_address
      },
      onShow() {
        this.showConfirmation = true
        this.model = new CustomerForm();
        this.showHideShippingBillingAddresses()
      },
      onHidden() {
        this.model = new CustomerForm();
        this.hideModal();
      },
      async onSubmit() {
        this.model.resetErrors();

        let response;
        this.loading = true;
        let requestData = this.model.getDataForCustomerFormSubmission();
        let success = true;
        if (requestData.status === 'blocked' && this.oldStatus !== requestData.status) {
          success = await this.$confirm(
              this.$t('If you block this customer they will be restricted from bidding, or participating in any of the ongoing or future auctions. NOTE: These restrictions might not apply to certain 3rd party auction providers, please inquire with Artisio support team for more details. Would you like to proceed?'),
          )
        }
        if (!success) {
          this.loading = false;
          return
        }
        if (!this.model.uuid) {
          response = await this.createCustomer(requestData);
        } else {
          response = await this.updateCustomer(requestData);
        }
        this.loading = false;
        if (response.success) {
          this.showConfirmation = false;
          this.hideModal();
          if (!this.model.uuid) {
            this.$toast('success', this.$t('Customer was successfully created'));
            if (this.modal.source !== SOURCE_BIDDERS_MODAL && this.modal.source !== SOURCE_RECEIPT_MODAL && this.modal.source !== SOURCE_CONDITION_REPORT_REQUEST_MODAL) {
              this.$router.push({
                name: 'customers.view',
                params: {id: response.body.uuid},
              });
            }
          } else {
            this.$toast('success', this.$t('Customer was successfully updated'));
            if (this.modal.source === SOURCE_CUSTOMER_VIEW || this.modal.source === SOURCE_CUSTOMER_DASHBOARD) {
              this['customerView/updateViewCustomer'](response.body);
              this.getAddressListAddresses();
            } else if (this.modal.source === SOURCE_INVENTORY_DASHBOARD) {
              this.updateInventoryDashboardCustomer(response.body);
            } else if (this.modal.source === SOURCE_RECEIPT_DASHBOARD) {
              this.updateViewReceiptCustomer(response.body);
            } else if (this.modal.source === SOURCE_RESERVATION_DASHBOARD) {
              this.reservationsUpdateVendor(response.body);
            }
          }
        } else {
          if (response.toastError) {
            this.$errorDialog(response.toastError);
          } else if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0])
          } else {
            this.model.setMultipleErrors(response.body);
          }
        }
      },
      onEmailChange() {
        this.model.defaultShippingAddress.email = !this.editObject ? this.model.email : this.model.defaultShippingAddress.email;
      },
      onPhoneChange() {
        this.model.defaultShippingAddress.phone = !this.editObject ? this.model.phone1 : this.model.defaultShippingAddress.phone;
      },
      copyAddress(from, to, showShippingAddress, showBillingAddress) {
        const oldCountryCode = _.cloneDeep(to.country_code);

        to.address_1 = from.address_1;
        to.address_2 = from.address_2;
        to.city = from.city;
        to.city_obj_uuid = from.city_obj_uuid;
        to.state = from.state;
        to.state_obj_uuid = from.state_obj_uuid;
        to.county = from.county;
        to.county_obj_uuid = from.county_obj_uuid;
        to.zip_code = from.zip_code;
        to.country_code = from.country_code;
        to.email = from.email;
        to.phone = from.phone;
        to.first_name = from.first_name;
        to.last_name = from.last_name;

        if (showShippingAddress) {
          this.model.show_shipping_address = true;
        }

        if (showBillingAddress) {
          this.model.show_billing_address = true;
        }
      },
      onNoEmailCheck() {
        this.$nextTick(() => {
          if (this.model.no_email) {
            this.model.email = `${uuid.v4()}@${this.customerDefaultEmailDomain}`;
          } else {
            this.model.email = null;
          }
        });
      }
    },
  }
</script>


<style lang="scss" scoped>
  @import "./scss/variables";

</style>
